import LogOutButton from '@components/log-out/log-out-button';
import { Walkthrough } from '@components/walkthrough/walkthrough';
import { Module } from '@interfaces/fc-user/permissions';
import { MenuActive } from '@modules/practitioner/practitioner-module';
import { RootState } from '@redux/store';
import { sections } from '@environments/access-roles';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Tour, { ReactourStep } from 'reactour';
import MenuItem from './menu-item';
import useUserCheckTrial from '@customHooks/fc-payment/use-user-check-trial';
type Props = {
    setHide: Dispatch<SetStateAction<boolean>>;
    hide: boolean;
};

interface IMenuItem
{
    route: string, 
    accessRoles: string[], 
    code: string, 
    label: string, 
    iconName: string, 
    module:Module
}

const DEFAULT_VALUES: IMenuItem[] = [
    {
        route: '/practitioner/calendar',
        accessRoles: sections.calendar,
        code: 'calendar',
        label: 'schedule',
        iconName: 'fad fa-calendar-alt',
        module: 'calendar'
    },
    {
        route: '/practitioner/patients',
        accessRoles: sections.patients,
        code: 'patients',
        label: 'patients',
        iconName: 'fad fa-users',
        module: 'patients'
    },
    {
        route: '/practitioner/public-profile',
        accessRoles: sections.publicProfile,
        code: 'public-profile',
        label: 'profile',
        iconName: 'fad fa-address-card',
        module: 'publicProfile'
    },
    {
        route: '/practitioner/offices',
        accessRoles: sections.offices,
        code: 'offices',
        label: 'offices',
        iconName: 'fas fa-hospital',
        module: 'offices'
    },
    {   //add access role name i.e. myServices to collections settings, permission settings and permissiondefault
        route: '/practitioner/services',
        accessRoles: sections.services,
        code: 'services',
        label: 'services',
        iconName: 'fad fa-stethoscope',
        module: 'services'
    },
    {
        route: '/practitioner/devices',
        accessRoles: sections.devices,
        code: 'devices',
        label: 'devices',
        iconName: 'fad fa-monitor-heart-rate',
        module: 'devices'
    },
    {
        route: '/practitioner/chat',
        accessRoles: sections.chat,
        code: 'chat',
        label: 'chat',
        iconName: 'fa fa-message',
        module: 'chat'
    },
    {
        route: '/practitioner/quiz',
        accessRoles: sections.quiz,
        code: 'quiz',
        label: 'quiz',
        iconName: 'fa fa-clipboard-question',
        module: 'quiz'
    },
    {
        route: '/practitioner/organization',
        accessRoles: sections.org,
        code: 'organization',
        label: 'org',
        iconName: 'fas fa-hospital',
        module: 'org'
    }
]

/** Menu entries available for users subscribed to the FREE plan */
const FREE_PLAN_MODULES: Module[] = ['calendar', 'publicProfile'];

const PractitionerMenu = (props: Props): JSX.Element => {
    const history = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(['practitionerMenu', 'walkthrough']);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const walkthrough = useSelector((state: RootState) => state.walkthrough);
    const organization = useSelector((state: RootState) => state.organizations);
    const session = useSelector((state: RootState) => state.session);
    const modules = useSelector((state: RootState) => state.practitionerModules);
    const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
    const { sectionActive, disableMenu, setMenuActive } = useContext(MenuActive);
    // Premium free trial or active coupon
    const { isTrial } = useUserCheckTrial();

    const [routesMenu, setRoutesMenu] = useState<IMenuItem[]>([]);

    /**
     * Filters the menus which the user has access to. 
     * 
     * This are determined in the Identity DB.
     */
    useEffect(() => {
        console.debug("Initial modules", modules.modules);
        const hasPremiumAccess = session.plan?.name === "PREMIUM" || isTrial === true;
        const accessModules: IMenuItem[] = DEFAULT_VALUES
            .filter(val => {
                const exists = modules.modules.some((m: Module) => m === val.module);
                if(!exists) return false;
                if(session.plan?.name === "FREE" && !isTrial && !FREE_PLAN_MODULES.includes(val.module)) return false;

                // This code is meant to be a replacement for 
                // https://github.com/healthprozone-code/dr-prox-front/blob/b95026da76bd5c39c96136c2ce5b4076421c3ce2/apps/dr-prox-front-react/src/components/page-wrapper/practitioner-menu/practitioner-menu.tsx#L142-L161
                // Organization management is only available for the PREMIUM plan, its free trial or a coupon
                if(val.module === "org" && (!organization.organizationType || !hasPremiumAccess))
                {
                    return false;
                }
                else return exists;
            });
        console.debug("Access modules", accessModules);
        setRoutesMenu(accessModules);
    }, [modules, organization, session.plan?.name, isTrial]);

    // console.log(routesMenu);
    const stepsTour: ReactourStep[] = [
        {
            selector: '[data-tut="reactour__goToServices"]',
            content: () => (
                <Walkthrough
                    action={() => navigateTo('/practitioner/services')}
                    title={t('walkthrough:init.goTo.title')}
                    description={t('walkthrough:init.goTo.text')}
                    titleButton={t('walkthrough:init.goTo.button')}
                />
            ),
        },
    ];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const disableBody = (target: any) => {
        disableBodyScroll(target);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const enableBody = (target: any) => {
        enableBodyScroll(target);
        closeTourModal();
    };
    const closeTourModal = () => {
        setIsTourOpen(false);
    };

    const navigateTo = (route: string) => {
        if (window.innerWidth < 1023) props.setHide(true);
        if(!disableMenu) {
            history(route);
        } else {
            setMenuActive(sectionActive, disableMenu, route)
        }
    };

    useLayoutEffect(() => {
        if (location.pathname !== '/practitioner/calendar') {
            setIsTourOpen(false);
        } else {
            if(!walkthrough.disabled){
                setIsTourOpen(true)
            }else{
                setIsTourOpen(false)
            }
            // setIsTourOpen(!walkthrough.disabled);
        }
    }, [walkthrough.disabled, location]);
    
    return (
        <div className="Menu">
            <div
                data-testid="hide-action"
                className="Menu__SectionsClose"
                onClick={() => {
                    props.setHide(true);
                }}
            >
                <em className="far fa-times"></em>
            </div>

            <div className="Menu__Sections">
                {routesMenu.map((item) =>
                    <MenuItem
                        key={item.code}
                        currentRoles={session.orgRoles?? ['OSA']}
                        accessRoles={item.accessRoles}
                        navigate={navigateTo}
                        route={item.route}
                        label={t(`${item.label}`)}
                        iconName={item.iconName}
                        isActive={sectionActive === item.code}
                        module = {item.module}
                    />
                )}
            </div>
            <LogOutButton></LogOutButton>
            <Tour
                steps={stepsTour}
                isOpen={isTourOpen}
                onRequestClose={closeTourModal}
                rounded={5}
                showButtons={false}
                showCloseButton={false}
                showNavigation={false}
                showNavigationNumber={false}
                goToStep={currentStep}
                showNumber={false}
                getCurrentStep={(curr) => {
                    setCurrentStep(curr);
                }}
                disableFocusLock={true}
                onAfterOpen={disableBody}
                onBeforeClose={enableBody}
            />
        </div>
    );
};
export default PractitionerMenu;
